import { getContactId } from '../utils/contactId';
import { useEffect, useRef, useCallback } from 'react';
import { getStatsigStableId, isExistingCustomerAnchoringEnabled, readSelfServeDecision, EXPERIMENTS } from '../utils/statsig';
import { buildMetaUserData } from '../utils/metaUserData';
import brandConfig from '../config/brand';
import { trackSubmitTelemetry } from '../utils/submitTelemetry';
import { splitAssignmentIdForPayload } from '../utils/splitTest';
import { pickSubmitEmail } from '../utils/pickSubmitEmail';
import { canAutoSubmitQuote, stampQuoteSubmission } from '../utils/quoteSubmission';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Hook to auto-submit quote when:
 * a) User closes the browser (fires instantly via sendBeacon)
 * b) 30 minutes of inactivity elapses
 *
 * @param {boolean} isActive - Whether the quote page is currently active
 * @param {Object} quoteData - The form data to submit
 * @param {Function} onAutoSubmit - Callback when auto-submit is triggered (for regular timeout case)
 * @param {string} zipCode - The user's zip code
 */
export const useAutoSubmitQuote = (isActive, quoteData, onAutoSubmit, zipCode, experimentVariant) => {
  const hasAutoSubmittedRef = useRef(false);
  const submissionPendingRef = useRef(false);
  const inactivityTimerRef = useRef(null);
  const quoteDataRef = useRef(quoteData);
  const zipCodeRef = useRef(zipCode);
  const isActiveRef = useRef(isActive);

  // Keep refs up to date with latest data
  useEffect(() => {
    quoteDataRef.current = quoteData;
    zipCodeRef.current = zipCode;
    isActiveRef.current = isActive;
  }, [quoteData, zipCode, isActive]);

  // Prepare payload for submission
  const preparePayload = useCallback((withSubmission = true) => {
    const data = quoteDataRef.current;
    if (!data || Object.keys(data).length === 0) return null;
    if (!data.serviceOption && !canAutoSubmitQuote()) return null;

    // Validate we have minimum required data
    if (!data.personName && !data.phone && !data.address) {
      console.warn('Auto-submit skipped: insufficient form data', {
        hasName: !!data.personName,
        hasPhone: !!data.phone,
        hasAddress: !!data.address
      });
      return null;
    }

    const decision = brandConfig.key === 'cabana'
      ? readSelfServeDecision(sessionStorage.getItem('force_experiment_variant')) : null;
    const variant = brandConfig.key === 'cabana'
      ? decision ? decision.showSelfServe ? 'treatment' : 'control' : '' : 'control';

    // Build the submission payload similar to fetchData in Step.jsx
    const payload = {
      ...data,
      autoSubmitted: true,
      quote_variant: variant,
      quote_experiment_name: decision?.assigned ? EXPERIMENTS.SELF_SERVE_FORM : '',
      selfServeQuoteAccepted: undefined,
      autoSubmitReason: 'browser_close_or_inactivity',
      token: 'CABANA-25266-TOKEN',
      contactId: getContactId() || null,
      statsig_user_id: sessionStorage.getItem('statsig_user_id') || null,
      statsig_stable_id: getStatsigStableId() || null,
      // Cabana (control) arm of the split test; undefined when not in test.
      split_assignment_id: splitAssignmentIdForPayload(),
      // sendBeacon builds its own payload, so the anchoring gate has to be
      // stamped here too or an abandoned quote reprices as a control lead.
      ec_anchoring: isExistingCustomerAnchoringEnabled(),
      site_origin: brandConfig.siteOrigin,
      // Stamp form version on the autosubmit payload too. fetchData sets this
      // for manual submits; sendBeacon builds its own payload so it has to
      // do it here. A service-option quote keeps the offer's version.
      formVersion: data.serviceOption && data.formVersion ? data.formVersion
        : variant === 'treatment'
        ? 'Test April 2026'
        : 'Control January 2026',
    };

    // Extract first and last name from personName
    if (payload.personName) {
      const nameParts = payload.personName.trim().split(' ');
      payload.firstName = nameParts[0] || "";
      payload.lastName = nameParts.slice(1).join(' ') || "";
    }

    // Capitalize pool type
    if (payload.poolType) {
      payload.poolType = payload.poolType.charAt(0).toUpperCase() + payload.poolType.slice(1);
    }

    // Convert service array to needHelpWith string
    const serviceLabels = {
      regular_pool_spa_service: "Regular pool or spa service",
      equipment_repair: "Equipment upgrade or repair",
      filter_or_salt_cell_cleaning: "Filter or salt cell cleaning",
      greentoclean: '"Green to clean" pool rescue',
      something_else: "Something else"
    };
    if (Array.isArray(payload.service)) {
      payload.needHelpWith = payload.service.map(item => serviceLabels[item] || item).join(", ");
    }

    // Add something else description if present
    if (payload.serviceSomethingElse) {
      payload.somethingElse = payload.serviceSomethingElse;
    }

    const pickedEmail = pickSubmitEmail(payload);
    if (pickedEmail) {
      payload.email = pickedEmail;
    }

    // Convert boolean add-on flags to Yes/No strings for Google Sheets and service-request
    if (typeof payload.has_filter_cleaning !== 'undefined') {
      payload.has_filter_cleaning = payload.has_filter_cleaning ? 'Yes' : 'No';
    }
    if (typeof payload.has_pool_rx !== 'undefined') {
      payload.has_pool_rx = payload.has_pool_rx ? 'Yes' : 'No';
    }
    if (typeof payload.has_heater_tuneup !== 'undefined') {
      payload.has_heater_tuneup = payload.has_heater_tuneup ? 'Yes' : 'No';
    }

    // Convert pool help array to equipmentHelp string
    const poolHelpLabels = {
      pool_pump: "Pool pump",
      pool_filter: "Pool filter",
      pool_heater: "Pool heater",
      salt_system: "Salt system",
      automaion_system: "Automation system",
      i_m_not_sure: "I'm not sure / something else"
    };
    if (Array.isArray(payload.poolHelp)) {
      payload.equipmentHelp = payload.poolHelp.map(item => poolHelpLabels[item] || item).join(", ");
    }
    if (payload.poolHelpNotSure) {
      payload.unsure = payload.poolHelpNotSure;
    }

    // Convert pool option to aboutPool string
    const poolOptionLabels = {
      pool_only: "Pool only",
      pool_spa: "Pool + attached spa",
      pool_hot_tub: "Pool + separate hot tub",
      hot_tub: "Above ground hot tub",
      something_else: "Something else"
    };
    if (payload.poolOption) {
      payload.aboutPool = poolOptionLabels[payload.poolOption] || payload.poolOption;
    }
    payload.has_spa = (payload.poolOption === 'pool_spa' || payload.poolOption === 'pool_hot_tub') ? 'Yes' : 'No';

    // Convert pool option1 array to aboutPoolType string
    const poolOption1Labels = {
      ground_pool: "I have an above ground pool",
      saltwater_pool: "I have a saltwater pool",
      trees_over_pool: "I have trees over the pool"
    };
    if (Array.isArray(payload.poolOption1)) {
      payload.aboutPoolType = payload.poolOption1.map(item => poolOption1Labels[item] || item).join(", ");
    }
    payload.has_salt_system = Array.isArray(payload.poolOption1) && payload.poolOption1.includes('saltwater_pool') ? 'Yes' : 'No';

    // Convert ga4_id to string to prevent Google Sheets from truncating it as a number
    if (payload.ga4_id) {
      payload.ga4_id = String(payload.ga4_id);
    }

    // Convert selectedServices array to comma-separated string for Google Sheets
    if (Array.isArray(payload.selectedServices)) {
      payload.selectedServices = payload.selectedServices.join(',');
    }

    // Add commercial zipCode if needed
    if (payload.poolType === 'Commercial' && payload.zipCode) {
      payload.commercialZipCode = payload.zipCode;
    }

    // Add UTM and ad tracking from localStorage (or sessionStorage fallback)
    const adKeys = ['utm_device', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'oppref'];
    adKeys.forEach(key => {
      const value = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (value) {
        payload[key] = value;
      }
    });

    // Meta CAPI session signals: read _fbp / _fbc cookies directly here so
    // we don't depend on any earlier localStorage sync. Cookies set by the
    // Meta Pixel on .cabanapools.com are visible from any subdomain.
    try {
      var _fbpMatch = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
      if (_fbpMatch) payload.meta_fbp = decodeURIComponent(_fbpMatch[1]);
      var _fbcMatch = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
      if (_fbcMatch) payload.meta_fbc = decodeURIComponent(_fbcMatch[1]);
      if (typeof navigator !== 'undefined' && navigator.userAgent) {
        payload.client_user_agent = navigator.userAgent;
      }
    } catch (e) {
      // Best-effort: a cookie/UA capture failure must not block auto-submit.
    }

    // Bot defense signals. Sync reads only here: this path also drives the
    // beforeunload / pagehide beacon, where awaiting would risk the browser
    // killing the tab before sendBeacon fires.
    try {
      const defense = typeof window !== 'undefined' ? window.__cabanaBotDefense : null;
      if (defense) {
        const token = defense.getTurnstileToken && defense.getTurnstileToken();
        if (token) payload.turnstile_token = token;
      }
    } catch (e) {
      // Best-effort: defense capture must not block auto-submit.
    }

    // Add source (document.referrer) from localStorage. The lead-processor
    // backup row and Sell attribution read the `referrer` / `landing_page`
    // keys (api/_lib/transform.js), which sat empty on every submission
    // before 2026-07-17 because only the legacy `source` key was sent.
    const source = localStorage.getItem('source');
    if (source) {
      payload.source = source;
      payload.referrer = source;
    }
    const landingPage = localStorage.getItem('landing_page');
    if (landingPage) {
      payload.landing_page = landingPage;
    }

    // Add GA4 ID from localStorage (captured on app load)
    const ga4Id = localStorage.getItem('ga4_id') || window.ga4_id;
    if (ga4Id) {
      payload.ga4_id = String(ga4Id);
    }

    // Add GA session ID from localStorage (captured on app load)
    // This was previously not possible in auto-submit, but now we pre-capture it
    const gaSessionId = localStorage.getItem('ga_session_id');
    if (gaSessionId) {
      payload.ga_session_id = gaSessionId;
    }

    return payload.serviceOption || !withSubmission ? payload : stampQuoteSubmission(payload);
  }, []);

  // Send data using beacon (for browser close - fires synchronously)
  const sendBeaconSubmit = useCallback((reason) => {
    // Guard: prevent double submission
    if (hasAutoSubmittedRef.current || submissionPendingRef.current) return false;

    // Guard: only submit if we're on the quote page
    if (!isActiveRef.current) return false;

    let payload;
    try { payload = preparePayload(); } catch { return false; }
    if (!payload) return false;

    // Mark as submitted BEFORE sending to prevent race conditions
    hasAutoSubmittedRef.current = true;

    payload.autoSubmitReason = reason;

    // GTM dataLayer push (synchronous, should work before unload)
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'formSubmitAttempt',
        'formStep': 'Quote Auto-Submitted',
        'formData': payload,
        'meta_user_data': buildMetaUserData(payload),
        'experimentVariant': experimentVariant || 'control',
        'quoteAmount': payload.amount,
        'autoSubmitted': true,
        'autoSubmitReason': reason
      });
    }

    try {
      // Unload-time submit: the page can never observe the outcome, so a
      // single beacon event records that the attempt happened at all.
      trackSubmitTelemetry('form_submit_beacon', {
        submit_kind: `auto_${reason}`,
        contact_id: payload.contactId || undefined,
      });
      const queued = navigator.sendBeacon(
        '/api/lead-submit',
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );

      if (!queued) { hasAutoSubmittedRef.current = false; return false; }

      // A queued beacon is an attempt, not a confirmed submission.
      if (payload.contactId) {
        navigator.sendBeacon(
          '/api/draft-save',
          new Blob([JSON.stringify({ ...payload, isSubmitted: false })], { type: 'application/json' })
        );
      }
    } catch (error) {
      // sendBeacon failed - log but don't block
      hasAutoSubmittedRef.current = false;
      console.error('Auto-submit sendBeacon failed:', error);
      return false;
    }

    return true;
  }, [preparePayload, experimentVariant]);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (!isActiveRef.current || hasAutoSubmittedRef.current) return;

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      // Double-check guards inside timeout (state may have changed)
      if (hasAutoSubmittedRef.current || submissionPendingRef.current || !isActiveRef.current) return;

      try { if (!preparePayload(false) || !onAutoSubmit) return; } catch { return; }
      hasAutoSubmittedRef.current = true;
      // Only the persistence callback can confirm the draft. A failure leaves
      // the next activity-triggered timeout available for the same request.
      Promise.resolve().then(() => submissionPendingRef.current ? false : onAutoSubmit('inactivity_timeout'))
        .then(saved => { if (saved !== true) hasAutoSubmittedRef.current = false; })
        .catch(() => { hasAutoSubmittedRef.current = false; });
    }, INACTIVITY_TIMEOUT);
  }, [onAutoSubmit, preparePayload]);

  // Set up beforeunload listener when quote is active
  useEffect(() => {
    if (!isActive) return;

    const handleBeforeUnload = () => {
      sendBeaconSubmit('browser_close');
    };

    // Handle page being frozen/discarded (mobile browsers, tab discarding)
    // Note: We do NOT auto-submit on simple visibility change (tab switch)
    // because the user may just be checking something else temporarily
    const handlePageHide = (event) => {
      // event.persisted is true if page is being cached (back/forward cache)
      // In that case, don't submit - user might come back
      if (event.persisted) return;
      sendBeaconSubmit('page_hide');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isActive, sendBeaconSubmit]);

  // Set up inactivity timer when quote is active
  useEffect(() => {
    if (!isActive) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    // Activity events to listen for
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Start the initial timer
    resetInactivityTimer();

    // Throttle activity handler to avoid excessive timer resets
    let lastActivityTime = Date.now();
    const THROTTLE_MS = 1000; // Only reset timer once per second max

    const throttledResetTimer = () => {
      const now = Date.now();
      if (now - lastActivityTime >= THROTTLE_MS) {
        lastActivityTime = now;
        resetInactivityTimer();
      }
    };

    // Add listeners for activity
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledResetTimer, { passive: true });
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledResetTimer);
      });
    };
  }, [isActive, resetInactivityTimer]);

  return {
    hasAutoSubmitted: hasAutoSubmittedRef.current,
    markAsSubmitted: () => { hasAutoSubmittedRef.current = true; },
    setSubmissionPending: pending => { submissionPendingRef.current = pending; }
  };
};

export default useAutoSubmitQuote;
